import { ChangeEvent, DataSource, DataSourceMode, Switch, TableRow } from "@mcleod/components";
import { ModelRow, StringUtil } from "@mcleod/core";
import { OrderRates } from "./OrderRates";
import { BillingRates } from "@mcleod/ar/src/BillingRates";

type RatesLayout = OrderRates | BillingRates;

/**
 * Utility class for managing taxable and backhaul tax switch interactions in Other Charges tables.
 * Handles the dependency logic between:
 * - Taxable switch (is_taxable)
 * - Backhaul Tax switch (is_backhaul_tax)
 * - Include in Freight switch (incl_in_freight)
 */
export class ChargeTaxUtil {
    private ratesLayout: RatesLayout;
    private ordersDatasource: DataSource;

    /**
     * Initialize the utility with the rates layout
     * @param ratesLayout - OrderRates or BillingRates instance
     */
    public initialize(ratesLayout: RatesLayout) {
        this.ratesLayout = ratesLayout;
        this.ordersDatasource = ratesLayout.mainDataSource;
    }

    /**
     * Event handler for when switchTaxable changes
     * @param event - The change event from the switch
     */
    public onSwitchTaxableChange(event: ChangeEvent): void {
        const tableRow = TableRow.getContainingTableRow(event.target as Switch);
        this.handleBackhaulTaxChange(tableRow);
    }

    /**
     * Event handler for when switchBackhaulTax changes
     * @param event - The change event from the switch
     */
    public onSwitchBackhaulTaxChange(event: ChangeEvent): void {
        const tableRow = TableRow.getContainingTableRow(event.target as Switch);
        this.handleBackhaulTaxChange(tableRow);
    }

    /**
     * Handle changes to backhaul tax switch - disable/enable include in freight.
     * When backhaul tax is checked, include in freight is disabled and unchecked.
     * When unchecked, include in freight is re-enabled based on customer settings.
     *
     * @param tableRow - The table row containing the switches
     */
    public handleBackhaulTaxChange(tableRow: TableRow): void {
        if (this.ordersDatasource.mode === DataSourceMode.SEARCH) {
            return;
        }

        const switchTaxable = tableRow.findComponentById("switchTaxable") as Switch;
        const switchBackhaulTax = tableRow.findComponentById("switchBackhaulTax") as Switch;
        const switchIncludeInFreight = tableRow.findComponentById("switchIncludeInFreight") as Switch;

        if (!switchBackhaulTax || !switchIncludeInFreight || !switchTaxable) return;

        const taxableChecked = switchTaxable.checked;
        switchBackhaulTax.enabled = taxableChecked;
        if (!taxableChecked) {
            switchBackhaulTax.checked = false;
        }
        if (switchBackhaulTax.checked) {
            switchIncludeInFreight.checked = false;
            switchIncludeInFreight.enabled = false;
        } else {
            // Only re-enable if customer allows it or no customer is selected
            const modelRow: ModelRow = tableRow.data as ModelRow;
            if (StringUtil.isEmptyString(modelRow.get("customer_id"))) {
                switchIncludeInFreight.enabled = this.getIncludeInFreightAllowed();
            }
        }
    }

    /**
     * Determines if include in freight should be allowed based on customer settings.
     * Different logic for OrderRates vs BillingRates due to different datasource structures.
     *
     * @returns true if include in freight is allowed, false otherwise
     */
    private getIncludeInFreightAllowed(): boolean {
        if (this.ratesLayout instanceof OrderRates) {
            // OrderRates has a sourceCustomer datasource
            const orderRates = this.ratesLayout as OrderRates;
            if (orderRates.sourceCustomer?.activeRow) {
                return orderRates.sourceCustomer.activeRow.get("allow_inc_in_frt", "N") === "Y";
            } else {
                return true;
            }
        } else {
            // BillingRates uses the main datasource with joined customer data
            return this.ordersDatasource?.activeRow?.get("customer.allow_inc_in_frt", "N") === "Y";
        }
    }

    /**
     * Process table row display event for other charges table.
     * Initializes the switch states based on current data.
     *
     * @param tableRow - The table row being displayed
     */
    public handleTableRowDisplay(tableRow: TableRow): void {
        this.handleBackhaulTaxChange(tableRow);
    }
}
